<template>
  <el-dialog
    v-model="dialogVisible"
    title="更换头像"
    width="400px"
    :close-on-click-modal="false"
    :before-close="handleClose"
  >
    <div class="upload-container">
      <div class="preview-wrapper" v-if="previewUrl || currentAvatar">
        <img :src="previewUrl || currentAvatar" :alt="'头像预览'" class="preview-image" />
      </div>
      
      <el-upload
        ref="uploadRef"
        class="upload-wrapper"
        action="#"
        :auto-upload="false"
        :show-file-list="false"
        accept="image/*"
        :on-change="handleFileChange"
        :before-upload="beforeUpload"
      >
        <el-button type="primary" class="select-btn">选择图片</el-button>
        <template #tip>
          <div class="el-upload__tip">
            支持JPG、PNG格式，大小不超过2MB
          </div>
        </template>
      </el-upload>
      
      <div class="info-text" v-if="selectedFile">
        已选择: {{ selectedFile.name }} ({{ formatFileSize(selectedFile.size) }})
      </div>
    </div>
    
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button 
          type="primary" 
          @click="handleUpload" 
          :loading="uploading"
          :disabled="!selectedFile"
        >
          {{ uploading ? '上传中...' : '确认上传' }}
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { updateUserAvatar, getUserProfile } from '@/user/api/userApi'; // 导入获取用户资料的API
import { useStore } from 'vuex'; // 导入useStore
import { withDefaults, defineProps, defineEmits } from 'vue';

interface Props {
  modelValue: boolean;
  currentAvatar?: string;
}

const props = withDefaults(defineProps<Props>(), {
  currentAvatar: ''
});

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'uploaded'): void;
  (e: 'close'): void;
}

const emit = defineEmits<Emits>();
const store = useStore(); // 使用store

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const selectedFile = ref<File | null>(null);
const previewUrl = ref<string | null>(null);
const uploading = ref(false);

// 处理文件选择变更
const handleFileChange = (file: any) => {
  const rawFile = file.raw;
  if (!rawFile) return;

  if (!beforeUpload(rawFile)) {
    return;
  }

  selectedFile.value = rawFile;
  
  // 创建预览URL
  if (rawFile) {
    previewUrl.value = URL.createObjectURL(rawFile);
  }
};

// 上传前校验
const beforeUpload = (file: File) => {
  // 检查文件类型
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
  if (!allowedTypes.includes(file.type)) {
    ElMessage.error('只能上传 JPG/PNG/GIF 格式的图片!');
    return false;
  }

  // 检查文件大小 (2MB)
  const maxSize = 2 * 1024 * 1024; // 2MB
  if (file.size > maxSize) {
    ElMessage.error('图片大小不能超过 2MB!');
    return false;
  }

  return true;
};

// 格式化文件大小
const formatFileSize = (size: number) => {
  if (size < 1024) {
    return size + 'B';
  } else if (size < 1024 * 1024) {
    return (size / 1024).toFixed(1) + 'KB';
  } else {
    return (size / 1024 / 1024).toFixed(1) + 'MB';
  }
};

// 执行上传
const handleUpload = async () => {
  if (!selectedFile.value) {
    ElMessage.warning('请选择要上传的图片');
    return;
  }

  try {
    uploading.value = true;
    
    const response = await updateUserAvatar(selectedFile.value);
    
    if (response.data.code === 1) {
      ElMessage.success('头像更新成功!');
      
      // 上传成功后，获取最新的用户资料并更新store
      try {
        const profileResponse = await getUserProfile();
        console.log('获取的用户资料完整响应:', profileResponse); // 添加调试日志
        
        // 根据API响应结构调整判断逻辑
        if (profileResponse && profileResponse.data && profileResponse.data.code === 1) {
          // 检查返回的用户资料中是否包含头像信息
          console.log('返回的用户资料数据:', profileResponse.data.data);
          console.log('返回的头像信息:', profileResponse.data.data?.avatar);
          
          // 更新用户信息到store（注意：API返回的数据结构是 {code: 1, data: {...}}）
          store.commit('SET_USER_INFO', profileResponse.data.data);
          console.log('用户资料已更新到store:', profileResponse.data.data);
        } else {
          console.error('获取用户资料失败:', profileResponse?.data?.msg || '未知错误');
        }
      } catch (error) {
        console.error('获取最新用户资料时发生错误:', error);
      }
      
      emit('uploaded'); // 不再传递URL，因为我们已经更新了store中的用户信息
      
      // 关闭弹窗
      handleClose();
    } else {
      ElMessage.error(response.data.msg || '头像更新失败');
    }
  } catch (error: any) {
    console.error('头像上传失败:', error);
    ElMessage.error(error.message || '头像上传失败，请稍后重试');
  } finally {
    uploading.value = false;
  }
};

// 关闭弹窗
const handleClose = () => {
  // 清理预览URL
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = null;
  }
  
  selectedFile.value = null;
  emit('close');
};
</script>

<style scoped>
.upload-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.preview-wrapper {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 20px;
  border: 1px solid #e4e7ed;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-wrapper {
  width: 100%;
  text-align: center;
}

.select-btn {
  width: 100%;
}

.info-text {
  margin-top: 15px;
  font-size: 14px;
  color: #606266;
  word-break: break-all;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
}
</style>